/**
 * One app, three faces.
 *
 * Not three apps: the same routes, the same auth, one deployment. Which face
 * you get is decided by the role on `/auth/me` — the server's answer, never
 * anything the client stored about itself.
 */
import { NavLink, Navigate, Route, Routes } from 'react-router-dom'

import { useSession } from './auth/session'
import { NotificationBell } from './notifications'
import {
  AdminDashboard,
  AdminOrganizationDetail,
  AdminOrganizations,
  AdminRequirementDetail,
  AdminRequirements,
} from './screens/Admin'
import { AdminFabrics } from './screens/AdminFabrics'
import { ForgotPassword, Register, SetPassword, SignIn, Waiting } from './screens/Auth'
import { BuyerRequirements, NewRequirement, RequirementDetail } from './screens/Buyer'
import { CompanyInbox, EnquiryDetail } from './screens/Company'
import { FabricSearch } from './screens/Fabrics'
import { Button, Loading, ThemeToggle } from './ui'

export default function App() {
  const { status, user, signOut } = useSession()

  // Held until the opening refresh answers, so a signed-in person reloading the
  // page never sees the sign-in screen flash past before their own.
  if (status === 'loading') {
    return (
      <div className="shell">
        <Loading />
      </div>
    )
  }

  if (status === 'signedOut' || !user) {
    return (
      <div className="shell">
        <ThemeToggle floating />
        <Routes>
          <Route path="/signin" element={<SignIn />} />
          <Route path="/register" element={<Register />} />
          <Route path="/forgot" element={<ForgotPassword />} />
          <Route path="/waiting" element={<Waiting />} />
          {/* Reachable without a session: it is how someone gets their first one. */}
          <Route path="/set-password" element={<SetPassword />} />
          <Route path="*" element={<Navigate to="/signin" replace />} />
        </Routes>
      </div>
    )
  }

  return (
    <div className="shell">
      <header className="topbar">
        <span className="brand">bjsfabric</span>
        <div className="topbar-actions">
          <NotificationBell />
          <ThemeToggle />
          <span className="whoami">
            {user.displayName}
            {/* The admin belongs to no organisation, so there is no second line
                to show — printing the role here just repeated their name. */}
            {user.organization ? (
              <>
                <br />
                {user.organization.name}
              </>
            ) : null}
          </span>
          <Button variant="quiet" onClick={() => void signOut()}>
            Sign out
          </Button>
        </div>
      </header>

      {user.role === 'BUYER' ? <BuyerFace /> : null}
      {user.role === 'COMPANY' ? <CompanyFace /> : null}
      {user.role === 'ADMIN' ? <AdminFace /> : null}
    </div>
  )
}

function BuyerFace() {
  return (
    <Routes>
      <Route path="/" element={<BuyerRequirements />} />
      <Route path="/new" element={<NewRequirement />} />
      <Route path="/fabrics" element={<FabricSearch />} />
      <Route path="/requirements/:id" element={<RequirementDetail />} />
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  )
}

function CompanyFace() {
  return (
    <Routes>
      <Route path="/" element={<CompanyInbox />} />
      <Route path="/enquiries/:id" element={<EnquiryDetail />} />
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  )
}

function AdminFace() {
  return (
    <>
      <Routes>
        <Route path="/" element={<AdminDashboard />} />
        <Route path="/requirements" element={<AdminRequirements />} />
        <Route path="/requirements/:id" element={<AdminRequirementDetail />} />
        <Route path="/organizations" element={<AdminOrganizations />} />
        <Route path="/organizations/:id" element={<AdminOrganizationDetail />} />
        <Route path="/fabrics" element={<AdminFabrics />} />
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>

      {/* Bottom bar: the admin is the one role with more than one place to be. */}
      <nav className="tabs">
        <NavLink to="/" end>
          Dashboard
        </NavLink>
        <NavLink to="/requirements">Requirements</NavLink>
        <NavLink to="/organizations">
          Buyers
          <br />
          Companies
        </NavLink>
        <NavLink to="/fabrics">Fabrics</NavLink>
      </nav>
    </>
  )
}
